// HARD RULE: Never synthesize an offer here. The section must only render
// when a real, backend-persisted offer with an active endDate exists.
// Synthetic discounts caused price/checkout mismatch and fake urgency.
// See mem://features/flash-deal-rules
import { Link } from "react-router-dom";
import { Zap, ArrowRight, ArrowLeft } from "lucide-react";
import { useProducts } from "@/contexts/ProductsContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { isOfferActive, calcDiscountedPrice, getDiscountLabel } from "@/utils/offerUtils";
import LazyImage from "@/components/LazyImage";
import CountdownTimer from "@/components/CountdownTimer";
import { optimizeImageUrl } from "@/utils/imageTransform";
import { productPath } from "@/utils/shareLinks";

const FLASH_BADGE = "⚡ Flash Deal";

const FlashDealSection = () => {
  const { products } = useProducts();
  const { isAr } = useLanguage();

  // Only render when a real, backend-persisted flash offer is active.
  const flashProduct = products.find(
    (p) =>
      isOfferActive(p.offer) &&
      (p.offer?.badge === FLASH_BADGE || p.offer?.badgeAr?.includes("خاطف"))
  );

  if (!flashProduct || !flashProduct.offer?.endDate) return null;

  const activeOffer = flashProduct.offer;
  const original = flashProduct.price;
  const discounted = calcDiscountedPrice(original, activeOffer);
  const discountLabel = getDiscountLabel(activeOffer, isAr);
  const currency = isAr ? "ج.م" : "EGP";
  const ArrowIcon = isAr ? ArrowLeft : ArrowRight;

  return (
    <section className="py-12 md:py-16 lg:py-20 bg-gradient-to-br from-primary/5 via-background to-accent/5 relative overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-full mb-4 animate-pulse">
            <Zap size={16} className="fill-current"  aria-hidden="true" focusable="false" />
            <span className="font-body text-xs font-bold tracking-widest uppercase">
              {isAr ? "عرض خاطف" : "Flash Deal"}
            </span>
          </div>
          <h2 className="font-serif text-3xl md:text-4xl font-bold mb-3">
            {isAr ? `${discountLabel} لمدة محدودة` : `${discountLabel}, Limited Time`}
          </h2>
          <p className="font-body text-muted-foreground">
            {isAr ? "عرض مختار بعناية, لا تفوّت الفرصة." : "A hand-picked offer. Don't miss out."}
          </p>
        </div>

        <Link
          to={productPath(flashProduct.id)}
          className="block max-w-3xl mx-auto bg-card border-2 border-primary/30 rounded-lg overflow-hidden shadow-xl hover:shadow-2xl hover:border-primary transition-all group"
        >
          <div className="grid md:grid-cols-2 gap-0">
            <div className="relative aspect-square md:aspect-auto md:h-full min-h-[280px] overflow-hidden bg-muted">
              <LazyImage
                src={optimizeImageUrl(flashProduct.image, { width: 800, quality: 75 })}
                alt={isAr ? flashProduct.nameAr : flashProduct.name}
                wrapperClassName="absolute inset-0 w-full h-full"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
            </div>
            <div className="p-6 md:p-8 flex flex-col justify-center">
              <div className="inline-block self-start px-3 py-1 bg-destructive/10 text-destructive rounded-full mb-3">
                <span className="font-body text-xs font-bold">{discountLabel}</span>
              </div>
              <h3 className="font-serif text-2xl md:text-3xl font-bold mb-3">
                {isAr ? flashProduct.nameAr : flashProduct.name}
              </h3>
              <p className="font-body text-sm text-muted-foreground mb-5 line-clamp-3">
                {isAr ? flashProduct.descriptionAr : flashProduct.description}
              </p>
              <div className="flex items-baseline gap-3 mb-5">
                <span className="font-serif text-3xl font-bold text-primary">
                  {discounted} {currency}
                </span>
                <span className="font-body text-base text-muted-foreground line-through">
                  {original} {currency}
                </span>
              </div>
              <div className="mb-5">
                <p className="font-body text-xs text-muted-foreground mb-2">
                  {isAr ? "ينتهي العرض خلال:" : "Offer ends in:"}
                </p>
                <CountdownTimer endDate={activeOffer.endDate} />
              </div>
              <button className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-body text-sm font-semibold tracking-wider uppercase py-3 rounded-full hover:bg-primary/90 transition-colors">
                {isAr ? "اشتري الآن" : "Shop Now"}
                <ArrowIcon size={16}  aria-hidden="true" focusable="false" />
              </button>
            </div>
          </div>
        </Link>
      </div>
    </section>
  );
};

export default FlashDealSection;
